
import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {Menu, X, User, LogOut, LogIn, Settings, Zap} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import EditableContent from './EditableContent'

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const location = useLocation()
  const { user, isAdmin, signIn, signOut, loading } = useAuth()

  // 현재 페이지가 편집 모드인지 확인하는 함수
  const getEditModeFromPage = () => {
    const currentPage = location.pathname.split('/')[1] || 'home'
    const editModeKey = `editMode_${currentPage}`
    return localStorage.getItem(editModeKey) === 'true'
  }

  const [isEditMode, setIsEditMode] = useState(getEditModeFromPage())

  // 페이지 변경 시 편집 모드 상태 업데이트
  useEffect(() => {
    setIsEditMode(getEditModeFromPage())
  }, [location.pathname])

  // 편집 모드 상태 변경 시 localStorage에 저장
  useEffect(() => {
    const currentPage = location.pathname.split('/')[1] || 'home'
    const editModeKey = `editMode_${currentPage}`
    localStorage.setItem(editModeKey, isEditMode.toString())
    
    // 페이지에 편집 모드 상태 전달을 위한 커스텀 이벤트 발생
    window.dispatchEvent(new CustomEvent('editModeChange', { 
      detail: { isEditMode, page: currentPage } 
    }))
  }, [isEditMode, location.pathname])

  const navigation = [
    { name: '홈', href: '/' },
    { name: '회사소개', href: '/about' },
    { name: '솔루션', href: '/solutions' },
    { name: '포트폴리오', href: '/portfolio' },
    { name: '뉴스', href: '/news' },
    { name: '자료실', href: '/resources' },
    { name: '지원', href: '/support' },
    { name: '문의', href: '/contact' }
  ]

  const handleLogin = async () => {
    try {
      await signIn()
    } catch (error) {
      console.error('Login error in header:', error)
    }
  }

  const handleLogout = async () => {
    try {
      await signOut()
      setIsUserMenuOpen(false)
    } catch (error) {
      console.error('Logout error in header:', error)
    }
  }

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode)
  }

  return (
    <header className="fixed top-0 left-0 right-0 bg-white shadow-lg z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* 로고 */}
          <Link to="/" className="flex items-center space-x-3">
            <EditableContent
              contentKey="header_logo"
              defaultContent="https://static.lumi.new/d2/d28c178c42320cd082a0530deccbf6bf.webp"
              contentType="image"
              className="h-10 object-contain"
              isEditMode={isEditMode}
            />
            <span className="text-xl font-bold text-gray-900">X:CONNECT</span>
          </Link>

          {/* 데스크톱 네비게이션 */}
          <nav className="hidden md:flex space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors ${
                  location.pathname === item.href ? 'text-blue-600 border-b-2 border-blue-600' : ''
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* 사용자 메뉴 & 편집 모드 토글 */}
          <div className="flex items-center space-x-4">
            {/* 관리자 전용 바로가기 버튼 */}
            {isAdmin && (
              <Link
                to="/admin"
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105"
                title="관리자 대시보드"
              >
                <Zap size={16} />
                <span className="hidden sm:block">관리자</span>
              </Link>
            )}

            {/* 편집 모드 토글 (관리자만) */}
            {isAdmin && (
              <button
                onClick={toggleEditMode}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105 ${
                  isEditMode 
                    ? 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white' 
                    : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white'
                }`}
                title={isEditMode ? '보기 모드로 전환' : '편집 모드로 전환'}
              >
                <Settings size={16} />
                <span className="hidden sm:block">{isEditMode ? '보기 모드' : '편집 모드'}</span>
              </button>
            )}

            {/* 로딩 상태 */}
            {loading ? (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            ) : user ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors"
                >
                  <User size={20} />
                  <span className="hidden sm:block">{user.userName || user.email}</span>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                    <div className="px-4 py-2 text-sm text-gray-700 border-b">
                      {user.email}
                      {isAdmin && (
                        <span className="block text-xs text-blue-600 font-medium">관리자</span>
                      )}
                    </div>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <User size={16} className="mr-2" />
                        관리자 페이지
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <LogOut size={16} className="mr-2" />
                      로그아웃
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleLogin}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
              >
                <LogIn size={16} />
                <span>로그인</span>
              </button>
            )}

            {/* 모바일 메뉴 버튼 */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-700 hover:text-blue-600"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* 모바일 메뉴 */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 text-base font-medium rounded-md transition-colors ${
                    location.pathname === item.href
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* 모바일 관리자 메뉴 */}
              {isAdmin && (
                <Link
                  to="/admin"
                  className="block px-3 py-2 text-base font-medium rounded-md text-purple-600 bg-purple-50 hover:bg-purple-100 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  관리자 페이지
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  )
}

export default Header
